import React, { Component } from "react"
import PropTypes from "prop-types"
import Chip from "./Chip"
import ChoiceCategoryHeader from "./ChoiceCategoryHeader"
import styles from "./MultiChoice.module.sass"
import LinkWithArrow from "lib/Components/LinkWithArrow"

class MultiChoice extends Component {
  static propTypes = {
    // Choices is an array of objects, either representing values nested under categories (1) or just values (2)
    // Supports only boolean attributes for now
    // 1st example: [
    //  {category: 'common_equipment', options: [{has_abs: true}]},
    //  {category: 'additional_equipment', options: [{has_xenon: true}]}
    // ]
    // 2nd example: [{has_abs: true}, {has_xenon: true}]
    choices: PropTypes.array,
    initiallySelected: PropTypes.object,
    doneCallback: PropTypes.func,
    labels: PropTypes.object,
    theme: PropTypes.object.isRequired,
    renderAppendToStep: PropTypes.func,
    trackFunction: PropTypes.func,
    buttonComponent: PropTypes.any,
    isSellYourCar: PropTypes.bool,
    stepName: PropTypes.string,
  }

  state = {
    selected: this.props.initiallySelected || {},
    expanded: false,
    allItemsFromCategorySelected: {
      ...Object.assign(
        {},
        ...this.props.choices.map((item) => ({
          [item.category.toString()]: false,
        }))
      ),
    },
  }

  componentDidMount() {
    const ButtonComponent = this.props.buttonComponent
    this.props.renderAppendToStep(
      ButtonComponent ? (
        <ButtonComponent onClick={this.finished}>
          {this.props.labels.carAssemblageDone}
        </ButtonComponent>
      ) : (
        <button
          type="button"
          onClick={this.finished}
          className={`
            main-cta
            ${this.props.isSellYourCar
              ? `${styles.sellYourCarConfirmButton} uds-button primary full-width`
              : `${styles.confirmButton}`
            }`}
        >
          {this.props.labels.continue}
        </button>
      )
    )
  }

  componentWillUnmount() {
    if (!this.props.buttonComponent) {
      this.props.renderAppendToStep(null)
    }
  }

  componentDidUpdate(prevProps) {
    // Because initiallySelected is set after this component is mounted, it
    // needs to set the state to reflect the initially selected
    if (
      this.props.initiallySelected !== prevProps.initiallySelected &&
      !Object.keys(this.state.selected).length
    ) {
      this.setState({ selected: this.props.initiallySelected })
    }
  }

  selectItem = (selected) => {
    this.setState((prevState) => ({
      selected: {
        ...prevState.selected,
        // Toggles the state of the item that got selected
        // E.g if state.selected contains {has_abs: true} and selected is {has_abs: true}
        // then the resulting state is {has_abs: false}
        ...Object.assign(
          {},
          ...Object.keys(selected).map((k) => ({ [k]: !prevState.selected[k] }))
        ),
      },
    }))
  }

  selectAllItems = (items, category) => {
    this.props.trackFunction({
      entity: "car_form",
      element: "equipment",
      action: "changed",
      details: "all selected",
      flow: "trade_in",
      type: "micro_proceeding",
    })
    this.setState((prevState) => ({
      selected: {
        ...prevState.selected,
        ...Object.assign(
          {},
          ...items.map((k) => ({
            [Object.keys(k.value)]:
              !prevState.allItemsFromCategorySelected[category],
          }))
        ),
      },
      allItemsFromCategorySelected: {
        ...prevState.allItemsFromCategorySelected,
        [category]: !prevState.allItemsFromCategorySelected[category],
      },
    }))
  }

  finished = () => {
    this.props.doneCallback(this.state.selected)
  }

  handleExpand = () => {
    this.setState((prevState) => ({ expanded: !prevState.expanded }))
  }

  renderChoices(choices, hideOthers) {
    if (hideOthers) {
      return (
        <React.Fragment>
          {choices.slice(0, 3).map(this.renderChoiceChip)}
          {this.state.expanded ? (
            choices.slice(3).map(this.renderChoiceChip)
          ) : null}
          <LinkWithArrow
            label={this.state.expanded ? this.props.labels.close : this.props.labels.seeAll}
            color={this.props.theme.color}
            onClick={() => {
              this.handleExpand()
              this.props.trackFunction({
                entity: "car_form",
                action: "clicked",
                element: `toggle all options for ${this.props.stepName}`,
                details: !this.state.expanded ? "show all" : "show less",
                flow: "trade_in",
                type: "micro_proceeding",
              })
            }}
            direction={this.state.expanded ? "up" : "down"}
          />
        </React.Fragment>
      )
    } else {
      return choices.map(this.renderChoiceChip)
    }
  }

  renderChoiceChip = (choice) => (
    <Chip
      key={choice.label}
      {...choice}
      theme={this.props.theme}
      onSelect={this.selectItem}
      isSelected={this.state.selected[Object.keys(choice.value)[0]]}
    />
  )

  render() {
    return (
      <React.Fragment>
        {this.props.choices && this.props.choices[0].category ? (
          this.props.choices.map((item, i) => (
            <React.Fragment key={item.category}>
              <ChoiceCategoryHeader>
                {item.category}{" "}
                <span
                  className={styles.selectAll}
                  style={{ color: this.props.theme.color }}
                  tabIndex={0}
                  role="button"
                  onClick={() =>
                    this.selectAllItems(item.options, item.category)
                  }
                  onKeyDown={(event) => {
                    if (event.key === " " || event.key === "Enter") {
                      this.selectAllItems(item.options, item.category)
                    }
                  }}
                >
                  (
                  {this.state.allItemsFromCategorySelected[item.category]
                    ? this.props.labels.deselect_all
                    : this.props.labels.select_all}
                  )
                </span>
              </ChoiceCategoryHeader>
              <div className={styles.chipList}>
                {this.renderChoices(item.options, i !== 0)}
              </div>
            </React.Fragment>
          ))
        ) : (
          <div className={styles.chipList}>
            {this.props.choices.map((item) => (
              <Chip
                key={item.label}
                label={item.label}
                value={item.value}
                onSelect={this.selectItem}
                isSelected={this.state.selected[Object.keys(item.value)[0]]}
              />
            ))}
          </div>
        )}
      </React.Fragment>
    )
  }
}

export default MultiChoice
